(ns common.opensearch.highlight
  "The parts of a text that a search found, for the marks in the grid and on the
   cards.

   The view marks the words of the query in each text: a word of 3 characters or
   more anywhere, as the trigram search finds it, and a shorter word at the start
   of a word. A typo has no text to mark, so OpenSearch marks the title for the
   view (`highlight-request`). Its marks are control characters, not HTML, so the
   view never parses HTML from a value."
  (:require
   [clojure.string :as string]))

(def mark-start
  "The character before a part that OpenSearch found."
  "\u0002")

(def mark-end
  "The character after a part that OpenSearch found."
  "\u0003")

(def ^:private inside-word-length
  "The shortest word that is marked inside a word. A shorter word is marked at
   the start of a word only, as the search finds it."
  3)

(defn highlight-request
  "The `highlight` part of an OpenSearch request that marks the whole value of
   each field of `fields`."
  [fields]
  {:pre_tags            [mark-start]
   :post_tags           [mark-end]
   :number_of_fragments 0
   :fields              (into {} (map (fn [field] [field {}])) fields)})

(defn query-words
  "The words of a search text: the parts between the spaces, lower case, with no
   repeat."
  [query-text]
  (->> (re-seq #"\S+" (or query-text ""))
       (map string/lower-case)
       (distinct)
       (vec)))

(defn- word-char?
  "True for a letter or a digit. A letter has two cases, in each alphabet, and the
   test works the same in Clojure and in ClojureScript."
  [c]
  (let [s (str c)]
    (boolean (and c
                  (or (re-matches #"[0-9]" s)
                      (not= (string/lower-case s) (string/upper-case s)))))))

(defn- word-start?
  [text index]
  (or (zero? index)
      (not (word-char? (nth text (dec index))))))

(defn- index-of
  "The index of `value` in `s` at `from` or after it, or nil."
  [s {:keys [value from]}]
  #?(:clj  (let [i (.indexOf ^String s ^String value (int from))]
             (when-not (neg? i) i))
     :cljs (let [i (.indexOf s value from)]
             (when-not (neg? i) i))))

(defn- ranges-of-word
  [lower-text word]
  (loop [from 0
         ranges []]
    (if-let [i (index-of lower-text {:value word :from from})]
      (recur (inc i)
             (if (or (<= inside-word-length (count word))
                     (word-start? lower-text i))
               (conj ranges [i (+ i (count word))])
               ranges))
      ranges)))

(defn merge-ranges
  "`ranges` sorted, with the ranges that overlap or touch merged."
  [ranges]
  (reduce (fn [merged [start end]]
            (let [[last-start last-end] (peek merged)]
              (if (and last-end (<= start last-end))
                (conj (pop merged) [last-start (max end last-end)])
                (conj merged [start end]))))
          []
          (sort ranges)))

(defn word-ranges
  "The ranges `[start end]` of `text` that have a word of `words`, see the
   namespace doc."
  [text words]
  (let [lower-text (string/lower-case (or text ""))]
    (->> words
         (remove string/blank?)
         (mapcat (partial ranges-of-word lower-text))
         (merge-ranges))))

(defn marked->ranges
  "The text and the ranges of a value that OpenSearch marked, see `mark-start`.
   Returns `{:text .. :ranges [[start end] ..]}`."
  [marked]
  (loop [chars (seq (or marked ""))
         text ""
         start nil
         ranges []]
    (if-let [c (first chars)]
      (cond
        (= (str c) mark-start) (recur (rest chars) text (count text) ranges)
        (= (str c) mark-end) (recur (rest chars) text nil (if start
                                                             (conj ranges [start (count text)])
                                                             ranges))
        :else (recur (rest chars) (str text c) start ranges))
      {:text   text
       :ranges ranges})))

(defn parts
  "`text` cut into parts `{:text .. :match? ..}` at the edges of `ranges`."
  [text ranges]
  (let [text (or text "")
        ranges (merge-ranges ranges)]
    (loop [position 0
           ranges ranges
           result []]
      (if-let [[start end] (first ranges)]
        (let [start (min (max start position) (count text))
              end (min end (count text))]
          (recur end
                 (rest ranges)
                 (cond-> result
                   (< position start) (conj {:text (subs text position start) :match? false})
                   (< start end) (conj {:text (subs text start end) :match? true}))))
        (cond-> result
          (< position (count text)) (conj {:text (subs text position) :match? false}))))))
